import { create } from 'zustand';
import type {
  GeoEvent, NewsItem, Earthquake, Wildfire, CountryRisk, MapViewState, RegionPreset,
  DataFreshness, Aircraft, Ship, NavalVessel, FighterJet, PowerPlant,
  ConflictZone, SubmarineCable, DroneZone, NuclearAsset, CrisisAlert, MarketQuote,
  TroopMovement, MissileTrajectory, AudienceProfile,
  CyberThreat, SocialSignal, EconomicEvent, BondYield, CreditSpread, SanctionEntry,
  DroneAttack, WorldCity, CountryProfile, StrategicChokepoint, CarrierGroup,
  CountryRivalry, AcledEvent, NotamItem,
} from '@/types';
import type { SimulationState, SimulationScenario, SimulationTurn, SimStatus } from '@/types/simulation';
import type { WindyWebcam } from '@/services/windy-webcams';
import type { BreakingAlert } from '@/algorithms/breaking-alert';
import type { SensorMode } from '@/components/map/sensor-optics';
import type { TrafficCamera } from '@/services/traffic-cameras';
import type { GlobeBasemapId } from '@/config/map-styles';
import { DEFAULT_GLOBE_BASEMAP } from '@/config/map-styles';
import { REGION_VIEWS, AUDIENCE_LAYER_PRESETS } from '@/types';

interface AppState {
  // === Map ===
  viewState: MapViewState;
  activeRegion: RegionPreset;
  setRegion: (region: RegionPreset) => void;
  setViewState: (vs: Partial<MapViewState>) => void;

  // === Core Data ===
  events: GeoEvent[];
  news: NewsItem[];
  asiaNews: NewsItem[];
  earthquakes: Earthquake[];
  wildfires: Wildfire[];
  countryRisks: CountryRisk[];
  setEvents: (e: GeoEvent[]) => void;
  setNews: (n: NewsItem[]) => void;
  setAsiaNews: (n: NewsItem[]) => void;
  setEarthquakes: (eq: Earthquake[]) => void;
  setWildfires: (wf: Wildfire[]) => void;
  setCountryRisks: (cr: CountryRisk[]) => void;

  // === Military / Transport / Infrastructure ===
  aircraft: Aircraft[];
  ships: Ship[];
  navalVessels: NavalVessel[];
  fighterJets: FighterJet[];
  powerPlants: PowerPlant[];
  conflictZones: ConflictZone[];
  submarineCables: SubmarineCable[];
  droneZones: DroneZone[];
  nuclearAssets: NuclearAsset[];
  setAircraft: (a: Aircraft[]) => void;
  setShips: (s: Ship[]) => void;
  setNavalVessels: (v: NavalVessel[]) => void;
  setFighterJets: (j: FighterJet[]) => void;
  setPowerPlants: (p: PowerPlant[]) => void;
  setConflictZones: (c: ConflictZone[]) => void;
  setSubmarineCables: (c: SubmarineCable[]) => void;
  setDroneZones: (d: DroneZone[]) => void;
  setNuclearAssets: (n: NuclearAsset[]) => void;

  // === Market Data ===
  marketQuotes: MarketQuote[];
  setMarketQuotes: (q: MarketQuote[]) => void;

  // === Crisis Alerts ===
  crisisAlerts: CrisisAlert[];
  setCrisisAlerts: (alerts: CrisisAlert[]) => void;
  dismissAlert: (id: string) => void;

  // === Breaking Alerts (X / Telegram real-time) ===
  breakingAlerts: BreakingAlert[];
  addBreakingAlerts: (alerts: BreakingAlert[]) => void;
  dismissBreakingAlert: (id: string) => void;
  markBreakingAlertAudioPlayed: (id: string) => void;

  // === Troops, Missiles, Drones ===
  troopMovements: TroopMovement[];
  missiles: MissileTrajectory[];
  droneAttacks: DroneAttack[];
  setTroopMovements: (t: TroopMovement[]) => void;
  setMissiles: (m: MissileTrajectory[]) => void;
  setDroneAttacks: (d: DroneAttack[]) => void;

  // === Intelligence (Recorded Future / Dataminr / Refinitiv / Bloomberg) ===
  cyberThreats: CyberThreat[];
  socialSignals: SocialSignal[];
  economicCalendar: EconomicEvent[];
  bondYields: BondYield[];
  creditSpreads: CreditSpread[];
  sanctions: SanctionEntry[];
  setCyberThreats: (c: CyberThreat[]) => void;
  setSocialSignals: (s: SocialSignal[]) => void;
  setEconomicCalendar: (e: EconomicEvent[]) => void;
  setBondYields: (b: BondYield[]) => void;
  setCreditSpreads: (c: CreditSpread[]) => void;
  setSanctions: (s: SanctionEntry[]) => void;

  // === ACLED Conflict Events ===
  acledEvents: AcledEvent[];
  setAcledEvents: (e: AcledEvent[]) => void;

  // === NOTAM Flight Restrictions ===
  notams: NotamItem[];
  setNotams: (n: NotamItem[]) => void;

  // === Military Aircraft (adsbexchange) ===
  militaryAircraft: Aircraft[];
  setMilitaryAircraft: (a: Aircraft[]) => void;

  // === Country Intelligence ===
  worldCities: WorldCity[];
  countryProfiles: CountryProfile[];
  allCountries: CountryProfile[];   // static (35) + REST Countries (195)
  chokepoints: StrategicChokepoint[];
  carrierGroups: CarrierGroup[];
  countryRivalries: CountryRivalry[];
  setWorldCities: (c: WorldCity[]) => void;
  setCountryProfiles: (p: CountryProfile[]) => void;
  setAllCountries: (c: CountryProfile[]) => void;
  setChokepoints: (c: StrategicChokepoint[]) => void;
  setCarrierGroups: (c: CarrierGroup[]) => void;
  setCountryRivalries: (r: CountryRivalry[]) => void;

  // === Country Compare (Option A flow) ===
  selectedCountry: CountryProfile | null;
  compareCountry: CountryProfile | null;
  compareMode: boolean;                    // true = waiting for second country click
  selectCountry: (c: CountryProfile | null) => void;
  setCompareCountry: (c: CountryProfile | null) => void;
  setCompareMode: (on: boolean) => void;
  clearCompare: () => void;

  // === Audience Profile ===
  activeProfile: AudienceProfile;
  setProfile: (p: AudienceProfile) => void;

  // === Webcams (Windy API) ===
  windyWebcams: WindyWebcam[];
  setWindyWebcams: (cams: WindyWebcam[]) => void;

  // === 전 세계 실시간 교통 CCTV ===
  trafficCameras: TrafficCamera[];
  setTrafficCameras: (c: TrafficCamera[]) => void;
  selectedCctvId: string | null;
  setSelectedCctvId: (id: string | null) => void;

  // === Layers ===
  visibleLayers: Set<string>;
  toggleLayer: (id: string) => void;
  setLayers: (ids: string[]) => void;

  // === Globe Mode ===
  globeMode: boolean;
  toggleGlobeMode: () => void;

  // === Tactical Optics (God's Eye 스타일 센서 뷰) ===
  globeBasemap: GlobeBasemapId;
  setGlobeBasemap: (b: GlobeBasemapId) => void;
  sensorMode: SensorMode;
  setSensorMode: (m: SensorMode) => void;
  hudEnabled: boolean;
  toggleHud: () => void;

  /** 커서가 올라간 맵 지점 — HUD 좌표 리드아웃용 */
  cursorGeo: { lon: number; lat: number } | null;
  setCursorGeo: (g: { lon: number; lat: number } | null) => void;

  /** 지도에서 클릭해 추적 중인 항공기 (id 로 보관 — 위치/고도가 실시간 갱신되므로) */
  selectedAircraftId: string | null;
  setSelectedAircraftId: (id: string | null) => void;

  /** 부팅 시퀀스 / 미션 선택 완료 여부 */
  bootComplete: boolean;
  completeBoot: () => void;

  // === Simulation Mode ===
  simulation: SimulationState;
  // Sim overlay layers (merged with real data in WorldMap)
  simMissiles: MissileTrajectory[];
  simTroopMovements: TroopMovement[];
  simConflictZones: ConflictZone[];
  setSimulationActive: (v: boolean) => void;
  setSimulationScenario: (s: SimulationScenario) => void;
  pushSimulationTurn: (turn: SimulationTurn) => void;
  setSimStatus: (s: SimStatus) => void;
  setStreamingText: (t: string) => void;
  setAutoMode: (v: boolean) => void;
  setSimMissiles: (m: MissileTrajectory[]) => void;
  setSimTroopMovements: (t: TroopMovement[]) => void;
  setSimConflictZones: (c: ConflictZone[]) => void;
  resetSimulation: () => void;

  // === UI ===
  selectedEvent: GeoEvent | null;
  selectEvent: (e: GeoEvent | null) => void;
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;

  // === Data Freshness ===
  freshness: Map<string, DataFreshness>;
  updateFreshness: (source: string, status: DataFreshness['status']) => void;

  // === Computed ===
  strategicRiskScore: () => number;
  computeThreatcon: () => number;
  activeEventCount: () => number;
  criticalAlerts: () => GeoEvent[];
}

export const useAppStore = create<AppState>((set, get) => ({
  // Map
  viewState: REGION_VIEWS.global,
  activeRegion: 'global',
  setRegion: (region) => set({
    activeRegion: region,
    viewState: REGION_VIEWS[region],
  }),
  setViewState: (vs) => set((s) => ({
    viewState: { ...s.viewState, ...vs },
  })),

  // Core Data
  events: [],
  news: [],
  asiaNews: [],
  earthquakes: [],
  wildfires: [],
  countryRisks: [],
  setEvents: (events) => set({ events }),
  setNews: (news) => set({ news }),
  setAsiaNews: (asiaNews) => set({ asiaNews }),
  setEarthquakes: (earthquakes) => set({ earthquakes }),
  setWildfires: (wildfires) => set({ wildfires }),
  setCountryRisks: (countryRisks) => set({ countryRisks }),

  // Military / Transport / Infrastructure
  aircraft: [],
  ships: [],
  navalVessels: [],
  fighterJets: [],
  powerPlants: [],
  conflictZones: [],
  submarineCables: [],
  droneZones: [],
  nuclearAssets: [],
  setAircraft: (aircraft) => set({ aircraft }),
  setShips: (ships) => set({ ships }),
  setNavalVessels: (navalVessels) => set({ navalVessels }),
  setFighterJets: (fighterJets) => set({ fighterJets }),
  setPowerPlants: (powerPlants) => set({ powerPlants }),
  setConflictZones: (conflictZones) => set({ conflictZones }),
  setSubmarineCables: (submarineCables) => set({ submarineCables }),
  setDroneZones: (droneZones) => set({ droneZones }),
  setNuclearAssets: (nuclearAssets) => set({ nuclearAssets }),

  // Market
  marketQuotes: [],
  setMarketQuotes: (marketQuotes) => set({ marketQuotes }),

  // Crisis Alerts
  crisisAlerts: [],
  setCrisisAlerts: (crisisAlerts) => set({ crisisAlerts }),
  dismissAlert: (id) => set((s) => ({
    crisisAlerts: s.crisisAlerts.map((a) =>
      a.id === id ? { ...a, dismissed: true } : a,
    ),
  })),

  // Breaking Alerts
  breakingAlerts: [],
  addBreakingAlerts: (newAlerts) => set((s) => {
    const existingIds = new Set(s.breakingAlerts.map((a) => a.id));
    const fresh = newAlerts.filter((a) => !existingIds.has(a.id));
    if (!fresh.length) return s;
    // Keep max 20 alerts in memory (oldest dismissed first)
    const combined = [...fresh, ...s.breakingAlerts].slice(0, 20);
    return { breakingAlerts: combined };
  }),
  dismissBreakingAlert: (id) => set((s) => ({
    breakingAlerts: s.breakingAlerts.map((a) =>
      a.id === id ? { ...a, dismissed: true } : a,
    ),
  })),
  markBreakingAlertAudioPlayed: (id) => set((s) => ({
    breakingAlerts: s.breakingAlerts.map((a) =>
      a.id === id ? { ...a, audioPlayed: true } : a,
    ),
  })),

  // Troops, Missiles, Drones
  troopMovements: [],
  missiles: [],
  droneAttacks: [],
  setTroopMovements: (troopMovements) => set({ troopMovements }),
  setMissiles: (missiles) => set({ missiles }),
  setDroneAttacks: (droneAttacks) => set({ droneAttacks }),

  // Intelligence data
  cyberThreats: [],
  socialSignals: [],
  economicCalendar: [],
  bondYields: [],
  creditSpreads: [],
  sanctions: [],
  setCyberThreats: (cyberThreats) => set({ cyberThreats }),
  setSocialSignals: (socialSignals) => set({ socialSignals }),
  setEconomicCalendar: (economicCalendar) => set({ economicCalendar }),
  setBondYields: (bondYields) => set({ bondYields }),
  setCreditSpreads: (creditSpreads) => set({ creditSpreads }),
  setSanctions: (sanctions) => set({ sanctions }),

  // ACLED
  acledEvents: [],
  setAcledEvents: (acledEvents) => set({ acledEvents }),

  // NOTAM
  notams: [],
  setNotams: (notams) => set({ notams }),

  // Military Aircraft
  militaryAircraft: [],
  setMilitaryAircraft: (militaryAircraft) => set({ militaryAircraft }),

  // Country Intelligence
  worldCities: [],
  countryProfiles: [],
  allCountries: [],
  chokepoints: [],
  carrierGroups: [],
  countryRivalries: [],
  setWorldCities: (worldCities) => set({ worldCities }),
  setCountryProfiles: (countryProfiles) => set({ countryProfiles }),
  setAllCountries: (allCountries) => set({ allCountries }),
  setChokepoints: (chokepoints) => set({ chokepoints }),
  setCarrierGroups: (carrierGroups) => set({ carrierGroups }),
  setCountryRivalries: (countryRivalries) => set({ countryRivalries }),

  // Country Compare
  selectedCountry: null,
  compareCountry: null,
  compareMode: false,
  selectCountry: (c) => set({ selectedCountry: c }),
  setCompareCountry: (c) => set({ compareCountry: c }),
  setCompareMode: (on) => set({ compareMode: on }),
  clearCompare: () => set({ selectedCountry: null, compareCountry: null, compareMode: false }),

  // Audience Profile
  activeProfile: 'INTEL',
  setProfile: (activeProfile) => set((s) => {
    const preset = AUDIENCE_LAYER_PRESETS[activeProfile];
    return {
      activeProfile,
      visibleLayers: new Set(preset),
    };
  }),

  // Webcams
  windyWebcams: [],
  setWindyWebcams: (windyWebcams) => set({ windyWebcams }),

  trafficCameras: [],
  setTrafficCameras: (trafficCameras) => set({ trafficCameras }),
  selectedCctvId: null,
  setSelectedCctvId: (selectedCctvId) => set({ selectedCctvId }),

  // Layers
  visibleLayers: new Set(['earthquakes', 'wildfires', 'war-zones', 'missiles', 'troop-movements', 'acled', 'traffic-cctv']),
  toggleLayer: (id) => set((s) => {
    const next = new Set(s.visibleLayers);
    next.has(id) ? next.delete(id) : next.add(id);
    return { visibleLayers: next };
  }),
  setLayers: (ids) => set({ visibleLayers: new Set(ids) }),

  // Globe Mode — God's Eye 기본값은 3D 글로브
  globeMode: true,
  toggleGlobeMode: () => set((s) => ({ globeMode: !s.globeMode })),

  // Tactical Optics
  globeBasemap: DEFAULT_GLOBE_BASEMAP,
  setGlobeBasemap: (globeBasemap) => set({ globeBasemap }),
  sensorMode: 'none',
  setSensorMode: (sensorMode) => set({ sensorMode }),
  hudEnabled: true,
  toggleHud: () => set((s) => ({ hudEnabled: !s.hudEnabled })),

  cursorGeo: null,
  setCursorGeo: (cursorGeo) => set({ cursorGeo }),

  selectedAircraftId: null,
  setSelectedAircraftId: (selectedAircraftId) => set({ selectedAircraftId }),

  bootComplete: false,
  completeBoot: () => set({ bootComplete: true }),

  // Simulation Mode
  simulation: {
    active: false,
    scenario: null,
    turns: [],
    currentTurn: 0,
    status: 'idle',
    streamingText: '',
    autoMode: false,
  },
  simMissiles: [],
  simTroopMovements: [],
  simConflictZones: [],
  setSimulationActive: (active) => set((s) => ({
    simulation: { ...s.simulation, active },
  })),
  setSimulationScenario: (scenario) => set((s) => ({
    simulation: { ...s.simulation, scenario },
  })),
  pushSimulationTurn: (turn) => set((s) => ({
    simulation: {
      ...s.simulation,
      turns: [...s.simulation.turns, turn],
      currentTurn: s.simulation.currentTurn + 1,
    },
  })),
  setSimStatus: (status) => set((s) => ({
    simulation: { ...s.simulation, status },
  })),
  setStreamingText: (streamingText) => set((s) => ({
    simulation: { ...s.simulation, streamingText },
  })),
  setAutoMode: (autoMode) => set((s) => ({
    simulation: { ...s.simulation, autoMode },
  })),
  setSimMissiles: (simMissiles) => set({ simMissiles }),
  setSimTroopMovements: (simTroopMovements) => set({ simTroopMovements }),
  setSimConflictZones: (simConflictZones) => set({ simConflictZones }),
  resetSimulation: () => set({
    simulation: {
      active: false,
      scenario: null,
      turns: [],
      currentTurn: 0,
      status: 'idle',
      streamingText: '',
      autoMode: false,
    },
    simMissiles: [],
    simTroopMovements: [],
    simConflictZones: [],
  }),

  // UI
  selectedEvent: null,
  selectEvent: (e) => set({ selectedEvent: e }),
  commandPaletteOpen: false,
  setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
  sidebarCollapsed: false,
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

  // Freshness
  freshness: new Map(),
  updateFreshness: (source, status) => set((s) => {
    const next = new Map(s.freshness);
    next.set(source, { source, lastFetch: Date.now(), status });
    return { freshness: next };
  }),

  // Computed
  strategicRiskScore: () => {
    const risks = get().countryRisks;
    const alerts = get().crisisAlerts.filter((a) => !a.dismissed);
    const top5 = [...risks].sort((a, b) => b.score - a.score).slice(0, 5);
    const weights = [0.35, 0.25, 0.20, 0.12, 0.08];
    let score = Math.round(top5.reduce((sum, r, i) => sum + r.score * (weights[i] || 0.05), 0));
    const alertBonus = alerts.reduce((s, a) => {
      if (a.level === 'extreme') return s + 8;
      if (a.level === 'critical') return s + 4;
      return s + 1;
    }, 0);
    return Math.min(100, score + alertBonus);
  },
  computeThreatcon: () => {
    const score = get().strategicRiskScore();
    if (score >= 85) return 5;
    if (score >= 70) return 4;
    if (score >= 50) return 3;
    if (score >= 30) return 2;
    return 1;
  },
  activeEventCount: () =>
    get().events.length + get().earthquakes.length + get().wildfires.length +
    get().aircraft.length + get().ships.length + get().navalVessels.length,
  criticalAlerts: () => get().events.filter((e) => e.severity === 'critical' || e.severity === 'high'),
}));
